import React, { useEffect, useRef } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { useDisplayNameSetup } from '@/hooks/useDisplayNameSetup';
import { eventBus } from '@/modules/core/events';
import { events as authEvents } from '@/modules/auth/events';
import { navigationState } from '@/modules/core/navigationState';
import { listState } from '@/modules/core/listState';
import { HomePage } from '@/app/pages/HomePage';
import { RequestsPage } from '@/app/pages/RequestsPage';
import { RequestDetailPage } from '@/app/pages/RequestDetailPage';
import { SubmitIdeaPage } from '@/app/pages/SubmitIdeaPage';
import { AccountPage } from '@/app/pages/AccountPage';
import { WhyZaptopiaPage } from '@/app/pages/WhyZaptopiaPage';
import { PrivacyPolicy } from '@/app/pages/PrivacyPolicy';
import { TermsOfService } from '@/app/pages/TermsOfService';
import { NotFoundPage } from '@/app/pages/NotFoundPage';
import { DisplayNameSetup } from '@/app/components/DisplayNameSetup';

// ScrollToTop component to handle scroll behavior
function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    // List of routes that should always scroll to top
    const alwaysScrollToTopRoutes = ['/privacy-policy', '/terms-of-service', '/why-zaptopia', '/submit', '/account'];
    
    if (alwaysScrollToTopRoutes.includes(location.pathname)) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  return null;
}

export default function App() {
  const { user, loading, isChecking } = useAuth();
  const { needsSetup, markSetupComplete } = useDisplayNameSetup();
  const location = useLocation();
  const hasSetupEventListener = useRef(false);

  useEffect(() => {
    // Prevent duplicate event subscriptions in React StrictMode
    if (hasSetupEventListener.current) {
      return;
    }

    const unsubscribe = eventBus.subscribe(authEvents.USER_SIGNED_IN, ({ user, restoreState }) => {
      console.log('User signed in, checking for state restoration:', { restoreState });
      
      // Clear any saved state after successful authentication
      navigationState.clear();
    });

    hasSetupEventListener.current = true;

    return () => {
      unsubscribe();
      hasSetupEventListener.current = false;
    };
  }, []);

  // Clear list state when navigating to routes that don't need it
  useEffect(() => {
    const routesThatClearListState = ['/account', '/why-zaptopia'];
    if (routesThatClearListState.includes(location.pathname)) {
      // console.log('App: Clearing list state for route:', location.pathname);
      listState.clear();
    }
  }, [location.pathname]);

  // Update page title and meta tags based on current route
  useEffect(() => {
    const updateMetaTags = () => {
      // Extract request ID from URL if we're on a request detail page
      const requestIdMatch = location.pathname.match(/^\/request\/(\d+)/);
      
      if (requestIdMatch) {
        // We'll update the title and meta tags when the request data loads
        // This will be handled in the RequestDetailPage component
        return;
      } else if (location.pathname === '/submit') {
        document.title = 'Submit App Idea - Zaptopia';
        const description = document.querySelector('meta[name="description"]');
        if (description) {
          description.setAttribute('content', 'Submit your app idea to the Zaptopia community. Share your vision and help build the apps you wish existed.');
        }
      } else if (location.pathname === '/account') {
        document.title = 'Account Settings - Zaptopia';
        const description = document.querySelector('meta[name="description"]');
        if (description) {
          description.setAttribute('content', 'Manage your Zaptopia account settings, update your display name and profile information.');
        }
      } else if (location.pathname === '/why-zaptopia') {
        document.title = 'How Zaptopia Works - Find or Create Any App';
        const description = document.querySelector('meta[name="description"]');
        if (description) {
          description.setAttribute('content', 'Learn how Zaptopia helps you find existing apps or request new ones that the community wants built.');
        }
      } else if (location.pathname === '/requests') {
        document.title = 'See What Apps People Actually Want - Zaptopia';
        const description = document.querySelector('meta[name="description"]');
        if (description) {
          description.setAttribute('content', 'Vote on ideas you\'d use or submit your own. See what apps people actually want built and help decide what gets created next.');
        }
      } else {
        document.title = 'Zaptopia - Does That App Exist? Find or Request Any App';
        const description = document.querySelector('meta[name="description"]');
        if (description) {
          description.setAttribute('content', 'Find existing apps that solve your problem or request new ones. Describe what you need - we\'ll find it or help you request it.');
        }
      }
    };

    updateMetaTags();
  }, [location.pathname]);

  if (loading || isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-violet-50">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-t-transparent border-blue-600 rounded-full animate-spin"></div>
          <div className="absolute inset-0 w-16 h-16 border-4 border-t-transparent border-violet-400 rounded-full animate-spin opacity-60" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* ScrollToTop component */}
      <ScrollToTop />

      {/* Display Name Setup Modal for New Users */}
      {user && needsSetup && (
        <DisplayNameSetup
          user={user}
          onComplete={markSetupComplete}
        />
      )}

      {/* Main App Routes */}
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/requests" element={<RequestsPage />} />
        <Route path="/request/:id" element={<RequestDetailPage />} />
        <Route path="/submit" element={<SubmitIdeaPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/why-zaptopia" element={<WhyZaptopiaPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      
      {/* Vercel Analytics */}
      <Analytics />
    </>
  );
}